import React from 'react';
import { FaGithub, FaEnvelope, FaInstagram } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import { ReactComponent as KairosLogo } from '../imgs/kairos_white.svg';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  const footerSections = [
    {
      title: t.footer.product,
      links: [
        { name: t.footer.links.features, href: '#diagnose' },
        { name: t.footer.links.pricing, href: '#get-started' },
        { name: t.footer.links.roiCalculator, href: '#calculator' },
      ]
    },
    {
      title: t.footer.company,
      links: [
        { name: t.footer.links.aboutUs, href: '/about' },
        { name: t.footer.links.ourProcess, href: '/#process' },
        { name: t.footer.links.contact, href: '#' }
      ]
    },
    {
      title: t.footer.legal,
      links: [
        { name: t.footer.links.privacy, href: '/legal#privacy' },
        { name: t.footer.links.terms, href: '/legal#terms' },
        { name: t.footer.links.cookies, href: '/legal#cookies' },
        { name: t.footer.links.gdpr, href: '/legal#gdpr' }
      ]
    }
  ];

  const socialLinks = [
    { icon: FaInstagram, href: 'https://instagram.com/kairossite', label: 'Instagram' },
    { icon: FaGithub, href: 'https://github.com/m-kaos/kairos', label: 'GitHub' },
    { icon: FaEnvelope, href: 'mailto:kairos@m-kaos.com', label: 'Email' }
  ];

  return (
    <footer
      className="pt-16 pb-8 relative"
      style={{ backgroundColor: 'var(--color-surface-dark)' }}
    >
      <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(99, 102, 241, 0.3), transparent 60%)', filter: 'blur(60px)' }}></div>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-8 overflow-hidden w-fit">
                <KairosLogo
                  className="h-full w-auto"
                  style={{
                    fill: 'url(#footer-logo-gradient)',
                    transform: 'scale(1.5)',
                    transformOrigin: 'center'
                  }}
                />
                <svg width="0" height="0">
                  <defs>
                    <linearGradient id="footer-logo-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" style={{ stopColor: 'var(--color-primary)', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: 'var(--color-accent)', stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <span className="text-2xl font-bold text-white">Kairos</span>
            </div>
            <p className="text-gray-400 text-sm mb-6">
              {t.footer.tagline}
            </p>
            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                  style={{
                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                    color: 'var(--color-primary-light)'
                  }}
                >
                  {React.createElement(social.icon, { className: "w-5 h-5" })}
                </a>
              ))}
            </div>
          </div>

          {/* Footer Sections */}
          {footerSections.map((section) => (
            <div key={section.title}>
              <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                {section.title}
              </h3>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className="text-gray-400 hover:text-white text-sm transition-colors duration-200"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
          <p className="text-gray-400 text-sm mb-4 md:mb-0">
            © {currentYear} {t.footer.copyright}
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <a href="/sitemap" className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
              {t.footer.links.sitemap}
            </a>
            <a href="/legal#accessibility" className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
              {t.footer.links.accessibility}
            </a>
            <a href="/status" className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
              {t.footer.links.status}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
